// src/hooks/useReviews.ts
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ReviewService } from '../admin/pages/review/reviewService';

export const REVIEW_KEYS = {
  all: ['reviews'] as const,
  lists: () => ['reviews', 'list'] as const,
  paginated: (page: number, search: string, rating: string) =>
    ['reviews', 'list', page, search, rating] as const,
  stats: () => ['reviews', 'stats'] as const,
};

// FIX: Removed the `cursors` field — seek-based pagination only needs `page`.
export interface ReviewPaginationParams {
  page: number;
  search: string;
  rating: string;
  pageSize: number;
  // `cursors` intentionally removed — seek-based pagination uses only `page`
}

// FIX: Passes `params.page` directly to getReviewsPaginated instead of cursor
// objects. Any page can now be fetched in any order.
export const useReviewsPaginated = (params: ReviewPaginationParams) => {
  return useQuery({
    queryKey: REVIEW_KEYS.paginated(params.page, params.search, params.rating),
    queryFn: () =>
      ReviewService.getReviewsPaginated(
        params.pageSize,
        params.page,
        params.rating !== 'all' ? parseInt(params.rating) : undefined,
        params.search || undefined
      ),
    staleTime: 1000 * 60 * 3,
    placeholderData: (prev: any) => prev,
  });
};

export const useReviewStats = () => {
  return useQuery({
    queryKey: REVIEW_KEYS.stats(),
    queryFn: () => ReviewService.getReviewStats(),
    staleTime: 1000 * 60 * 5,
  });
};

export const useReviewMutations = () => {
  const queryClient = useQueryClient();

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: REVIEW_KEYS.all });
  };

  const remove = useMutation({
    mutationFn: (reviewId: string) => ReviewService.deleteReview(reviewId),
    onSuccess: invalidateAll,
  });

  return { remove };
};